import { Observable, Subscriber } from 'rxjs';
import {
  ApNotificationService,
  ApSnackBarService,
  ApUndoableOperationService,
  PendingUndoableOperation,
  RunningOperation,
} from 'webcommon/core-ui';
import { ApBaseClass, FindControlDeleteItemResultDto, FindControlDeleteItemStatus, IColumnPk } from 'webcommon/shared';
import { ApFindControlService } from '../ap-findcontrol.service';
import { FcMergeItemModalService } from './fc-merge-item-modal.service';

export enum FindControlDeleteAction {
  Predelete,
  PredeleteFailure,
  Blocked,
  Deleting,
  Deleted,
  DeleteFailure,
  Undo,
}

export interface FindControlDeleteCheckpoint {
  dataItem: IColumnPk;
  action: FindControlDeleteAction;
}

export class DeleteFindControlItemOperation extends ApBaseClass {
  private subscriber: Subscriber<FindControlDeleteCheckpoint>;
  private observable: Observable<FindControlDeleteCheckpoint>;

  constructor(
    private apNotificationService: ApNotificationService,
    private apFindControlService: ApFindControlService,
    private apSnackBarService: ApSnackBarService,
    private apUndoableOperationService: ApUndoableOperationService,
    private undoComponentId: number,
    private findControlBehaviorId: string,
    private findControlBehaviorName: string,
    private primaryKeyColumnName: string,
    private dataItem: IColumnPk,
    private fcMergeItemModalService: FcMergeItemModalService
  ) {
    super();

    this.subscriber = new Subscriber<FindControlDeleteCheckpoint>();
    this.observable = new Observable<FindControlDeleteCheckpoint>((subscriber) => {
      this.subscriber = subscriber;
      this.start();
    });
  }

  getObservable() {
    return this.observable;
  }

  private async start() {
    this.apUndoableOperationService.queueOperationAsync(this.undoComponentId, async (skipUndo: boolean) => {
      return await this.initiateDeleteWorkflow(skipUndo);
    });
  }

  private async initiateDeleteWorkflow(skipUndo: boolean): Promise<RunningOperation> {
    try {
      const canDeleteResult = await this.apFindControlService.canDeleteItem(this.findControlBehaviorId, this.dataItem.Pk).toPromise();
      switch (canDeleteResult.DeleteItemStatus) {
        case FindControlDeleteItemStatus.CanBeDeleted: {
          this.subscriber.next({
            action: FindControlDeleteAction.Predelete,
            dataItem: this.dataItem,
          });

          if (this.subscriber.closed) {
            return {
              completionPromise: Promise.resolve(),
            };
          }

          this.subscriber.next({
            action: FindControlDeleteAction.Deleting,
            dataItem: this.dataItem,
          });

          if (this.subscriber.closed) {
            return {
              completionPromise: Promise.resolve(),
            };
          } else if (skipUndo) {
            return {
              completionPromise: this.deleteItem(),
            };
          } else {
            const newPendingUndoableOperation = this.createUndoDeleteOperation();
            return {
              completionPromise: newPendingUndoableOperation.onCompleted(),
              pendingUndo: newPendingUndoableOperation,
            };
          }
        }
        case FindControlDeleteItemStatus.InUseAndCannotBeDeleted: {
          this.subscriber.next({
            action: FindControlDeleteAction.PredeleteFailure,
            dataItem: this.dataItem,
          });
          this.subscriber.complete();
          this.apNotificationService.warning(canDeleteResult.ResultMessage);
          return {
            completionPromise: Promise.resolve(),
          };
        }
        case FindControlDeleteItemStatus.InUseAndCanBeMerged: {
          this.subscriber.next({
            action: FindControlDeleteAction.Predelete,
            dataItem: this.dataItem,
          });

          this.subscriber.next({
            action: FindControlDeleteAction.Deleting,
            dataItem: this.dataItem,
          });

          if (this.subscriber.closed) {
            return {
              completionPromise: Promise.resolve(),
            };
          } else {
            return {
              completionPromise: this.openMergeItemModal(),
            };
          }
        }
        case FindControlDeleteItemStatus.DeleteFailed: {
          this.subscriber.next({
            action: FindControlDeleteAction.DeleteFailure,
            dataItem: this.dataItem,
          });
          this.subscriber.complete();
          this.apNotificationService.warning(canDeleteResult.ResultMessage);
          return {
            completionPromise: Promise.resolve(),
          };
        }
        default: {
          throw new Error('Unknown result status returned by canDeleteItem endpoint.');
        }
      }
    } catch (err) {
      this.subscriber.next({
        action: FindControlDeleteAction.DeleteFailure,
        dataItem: this.dataItem,
      });
      this.subscriber.complete();
      this.apNotificationService.displayStandardError();

      throw err;
    }
  }

  private createUndoDeleteOperation() {
    const snackBarRef = this.apSnackBarService.showUndoSnackBar(`${this.dataItem.Name} Selection Deleted`);
    return new PendingUndoableOperation(
      undefined,
      () => this.undoDelete(),
      () => this.deleteItem(),
      snackBarRef,
    );
  }

  private undoDelete() {
    this.subscriber.next({
      action: FindControlDeleteAction.Undo,
      dataItem: this.dataItem,
    });
    this.subscriber.complete();

    return Promise.resolve<FindControlDeleteItemResultDto | undefined>(undefined);
  }

  private async deleteItem(): Promise<FindControlDeleteItemResultDto> {
    try {
      const deleteItemResult = await this.apFindControlService.deleteItem(this.findControlBehaviorId, this.dataItem.Pk).toPromise();
      switch (deleteItemResult.DeleteItemStatus) {
        case FindControlDeleteItemStatus.Deleted: {
          this.subscriber.next({
            action: FindControlDeleteAction.Deleted,
            dataItem: this.dataItem,
          });
          this.subscriber.complete();
          break;
        }
        case FindControlDeleteItemStatus.InUseAndCannotBeDeleted: {
          this.subscriber.next({
            action: FindControlDeleteAction.DeleteFailure,
            dataItem: this.dataItem,
          });
          this.subscriber.complete();
          this.apNotificationService.warning(deleteItemResult.ResultMessage);
          break;
        }
        case FindControlDeleteItemStatus.InUseAndCanBeMerged: {
          const mergeItemResult = await this.openMergeItemModal();
          return mergeItemResult;
        }
        case FindControlDeleteItemStatus.DeleteFailed: {
          this.subscriber.next({
            action: FindControlDeleteAction.DeleteFailure,
            dataItem: this.dataItem,
          });
          this.subscriber.complete();
          this.apNotificationService.warning(deleteItemResult.ResultMessage);
          break;
        }
        default: {
          throw new Error('Unknown result status returned by deleteItem endpoint.');
        }
      }

      return deleteItemResult;
    }
    catch (err) {
      this.subscriber.next({
        action: FindControlDeleteAction.DeleteFailure,
        dataItem: this.dataItem,
      });
      this.subscriber.complete();
      this.apNotificationService.displayStandardError();

      throw err;
    }
  }

  private async openMergeItemModal(): Promise<FindControlDeleteItemResultDto> {
      let deleteItemResultDto: FindControlDeleteItemResultDto;
      const modalResult = await this.fcMergeItemModalService.openModal(
        this.findControlBehaviorId, this.findControlBehaviorName, this.primaryKeyColumnName, this.dataItem);

      if (modalResult.isSuccess) {
        if (modalResult.resultValue.DeleteItemStatus === FindControlDeleteItemStatus.Deleted) {
          // Item was successfully replaced and then deleted
          this.subscriber.next({
            action: FindControlDeleteAction.Deleted,
            dataItem: this.dataItem,
          });
          this.apNotificationService.success(modalResult.resultValue.ResultMessage);
        } else {
          // Item could not be merged.
          this.subscriber.next({
            action: FindControlDeleteAction.DeleteFailure,
            dataItem: this.dataItem,
          });
          this.apNotificationService.warning(modalResult.resultValue.ResultMessage);
        }

        this.subscriber.complete();
        deleteItemResultDto = modalResult.resultValue;
      } else {
        // User cancelled the merge process
        this.subscriber.next({
          action: FindControlDeleteAction.DeleteFailure,
          dataItem: this.dataItem,
        });
        this.subscriber.complete();
        deleteItemResultDto = {
          ItemId: this.dataItem.Pk,
          ResultMessage: `User cancelled replace action.`,
          DeleteItemStatus: FindControlDeleteItemStatus.DeleteFailed,
        };
      }

      return deleteItemResultDto;
  }
}
